import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary-700 text-white hover:bg-primary-800 dark:glow-primary dark:hover:glow-primary-hover',
        destructive:
          'bg-red-500 text-white hover:bg-red-600',
        outline:
          'border border-border-subtle-light dark:border-border-subtle-dark bg-transparent hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
        secondary:
          'bg-surface-hover-light text-text-primary-light hover:bg-surface-base-light dark:bg-surface-raised-dark dark:text-text-primary-dark dark:hover:bg-surface-base-dark',
        ghost:
          'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
        link:
          'text-primary-500 underline-offset-4 hover:underline',
        inline:
          'text-text-secondary-light dark:text-text-secondary-dark underline-offset-4 hover:underline hover:text-text-primary-light dark:hover:text-text-primary-dark text-sm p-0 h-auto',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-lg px-8',
        icon: 'h-9 w-9',
        xs: 'h-7 rounded-md px-2.5 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
